/**
 * urlParams.ts - URL parameter handling for single-tool mode
 * 
 * CHANGE HISTORY:
 * - 251101_0715 [pending] danielbeer: URL parameter utilities for FileMaker integration
 * 
 * Usage in FileMaker:
 * - Single tool: ?tool=pdf-viewer
 * - With data: ?tool=data-parser&data=base64EncodedData
 * - Hide nav: ?tool=rmemb&hideNav=true
 */

import { ToolType } from '../types';

export interface URLParams {
  tool?: ToolType;
  data?: string;
  hideNav?: boolean;
  config?: string;
  samples?: string;
  showToolbar?: string;
  customize?: string;
}

/**
 * Parse URL parameters for FileMaker WebViewer integration
 */
export function parseURLParams(): URLParams {
  const params = new URLSearchParams(window.location.search);
  
  return {
    tool: params.get('tool') as ToolType || undefined,
    data: params.get('data') || undefined,
    hideNav: params.get('hideNav') === 'true',
    config: params.get('config') || undefined
  };
}

/**
 * Decode base64 data from FileMaker
 */
export function decodeData(base64Data: string): any {
  try {
    const decoded = atob(base64Data);
    return JSON.parse(decoded);
  } catch (err) {
    console.error('Failed to decode data:', err);
    return null;
  }
}

/**
 * Encode data for FileMaker
 */
export function encodeData(data: any): string {
  try {
    const json = JSON.stringify(data);
    return btoa(json);
  } catch (err) {
    console.error('Failed to encode data:', err);
    return '';
  }
}

/**
 * Build FileMaker WebViewer URL
 * 
 * Example FileMaker calculation:
 * "https://yourserver.com/fmwvtool?tool=data-parser&data=" & Base64Encode ( JSONFormatElements ( $data ) )
 */
export function buildWebViewerURL(tool: ToolType, data?: any, hideNav: boolean = true): string {
  const base = window.location.origin + window.location.pathname;
  const params = new URLSearchParams();
  
  params.set('tool', tool);
  if (hideNav) params.set('hideNav', 'true');
  if (data) params.set('data', encodeData(data));
  
  return `${base}?${params.toString()}`;
}

/**
 * Example FileMaker script calculations for each tool
 */
export const FILEMAKER_EXAMPLES = {
  'pdf-viewer': `
// FileMaker Script: Open PDF Viewer
Let ( [
  ~url = "https://yourserver.com/fmwvtool?tool=pdf-viewer&hideNav=true"
] ;
  // Set WebViewer to URL
  ~url
)`,

  'data-parser': `
// FileMaker Script: Parse CSV Data
Let ( [
  ~data = JSONSetElement ( "{}" ; 
    ["input" ; YourTable::CSVData ; JSONString ] ;
    ["format" ; "csv" ; JSONString ]
  ) ;
  ~encoded = Base64Encode ( ~data ) ;
  ~url = "https://yourserver.com/fmwvtool?tool=data-parser&hideNav=true&data=" & ~encoded
] ;
  ~url
)`,

  'rmemb': `
// FileMaker Script: Store Data in Rmemb
Let ( [
  ~data = JSONSetElement ( "{}" ;
    ["key" ; YourTable::RecordID ; JSONString ] ;
    ["value" ; YourTable::Data ; JSONString ] ;
    ["tags" ; YourTable::Tags ; JSONString ]
  ) ;
  ~encoded = Base64Encode ( ~data ) ;
  ~url = "https://yourserver.com/fmwvtool?tool=rmemb&hideNav=true&data=" & ~encoded
] ;
  ~url
)`,

  'table-viewer': `
// FileMaker Script: Display Records in Table
Let ( [
  ~records = JSONListValues ( YourTable::JSONRecords ; "" ) ;
  ~data = JSONSetElement ( "{}" ; ["records" ; ~records ; JSONArray ] ) ;
  ~encoded = Base64Encode ( ~data ) ;
  ~url = "https://yourserver.com/fmwvtool?tool=table-viewer&hideNav=true&data=" & ~encoded
] ;
  ~url
)`,

  'filemaker-bridge': `
// FileMaker Script: Open Bridge (no data needed)
Let ( [
  ~url = "https://yourserver.com/fmwvtool?tool=filemaker-bridge&hideNav=true"
] ;
  ~url
)`
};
